// src/pages/MasterInventory.js
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiPlus, FiX, FiArrowLeft, FiChevronDown, FiChevronUp, FiImage, FiGrid, FiLayers, FiSearch } from 'react-icons/fi';
import { QRCodeCanvas } from 'qrcode.react';
import ListView from '../components/ListView';
import CustomFileUpload from '../components/CustomFileUpload';
import { useIsMobile } from '../hooks/useIsMobile'; 

// =================================================================================
// QR CODE MODAL
// =================================================================================
const QRCodeModal = ({ isOpen, onClose, item }) => {
    if (!isOpen || !item) return null;
    const checkoutUrl = `${window.location.origin}/inventory/checkout/${item._id}`;

    return (
        <div className="modal-overlay printable-modal" onClick={onClose}>
            <div className="modal-content modal-content-slim" onClick={e => e.stopPropagation()} style={{textAlign: 'center'}}>
                <div className="modal-header">
                    <h3>QR Code: {item.sku || 'No SKU'}</h3>
                    <button onClick={onClose} className="modal-close-btn"><FiX /></button>
                </div>
                <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem', padding: '2rem' }}>
                    <div style={{ padding: '1rem', background: 'white', borderRadius: '8px', border: '1px solid #ccc' }}>
                        <QRCodeCanvas value={checkoutUrl} size={200} level={"H"} />
                    </div>
                    <div>
                        <strong>{item.name}</strong>
                        <p className="item-meta">Scan to check out this item</p>
                        <p className="item-meta" style={{fontSize: '0.7rem', color: '#999', wordBreak: 'break-all'}}>
                            {checkoutUrl}
                        </p>
                    </div>
                </div>
                <div className="modal-footer">
                    <button className="btn btn-secondary" onClick={onClose}>Close</button>
                    <button className="btn btn-primary" onClick={() => window.print()}>Print</button>
                </div>
            </div>
        </div>
    );
};

// =================================================================================
// MOBILE MASTER ITEM CARD
// =================================================================================
const MobileMasterItemCard = ({ item, onClick, onShowQR }) => {
    return (
        <div className="mobile-inventory-card" onClick={onClick} style={{ cursor: 'pointer' }}>
            <div className="mobile-card-main">
                <div className="list-view-image-thumbnail-wrapper" style={{ width: '60px', height: '60px', flexShrink: 0 }}>
                    {item.imageUrl ? (
                        <img src={item.imageUrl} alt={item.name} className="list-view-image-thumbnail" />
                    ) : (
                        <FiImage className="list-view-image-placeholder-icon" style={{ fontSize: '24px' }} />
                    )}
                </div>
                <div className="mobile-card-info">
                    <h4 className="mobile-item-name">{item.name}</h4>
                    {/* ✅ FIXED: Changed color from var(--blue-600) to var(--gray-500) for contrast */}
                    {item.manufacturer && <span className="mobile-item-sku" style={{display:'block', fontSize:'0.75rem', color:'var(--gray-500)'}}>{item.manufacturer}</span>}
                    <span className="mobile-item-sku">SKU: {item.sku || 'N/A'}</span>
                </div>
                <div className="mobile-card-quantity">
                    <span className="qty-label">Cost</span>
                    <span className="qty-value" style={{ fontSize: '1.1rem' }}>${(item.unitCost || 0).toFixed(2)}</span>
                </div>
            </div>
            <div className="mobile-card-footer">
                <div className="mobile-card-meta">
                    <span className="tag">{item.category}</span>
                    <span className="tag type-inventory">{item.itemType === 'High Value Equipment' ? 'High Value' : item.itemType}</span>
                </div>
                <button className="btn btn-secondary btn-sm" onClick={(e) => { e.stopPropagation(); onShowQR(item); }}>
                    <FiGrid className="btn-icon" /> QR
                </button>
            </div>
        </div>
    );
};

// =================================================================================
// MASTER ITEM FORM
// =================================================================================
const MasterItemForm = ({
    formData,
    onFormChange,
    onSave,
    onCancel,
    isEditMode
}) => {
    const [isAdvancedView, setIsAdvancedView] = useState(() => {
        return isEditMode && (
            formData.category !== 'Parts' ||
            formData.reorderLevel !== 10 ||
            formData.unitCost !== 0 ||
            formData.buyAgainLink ||
            formData.imageUrl
        );
    });

    const [selectedFile, setSelectedFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(formData.imageUrl || '');

    useEffect(() => {
        setPreviewUrl(formData.imageUrl || '');
        setSelectedFile(null); 
    }, [formData.imageUrl, isEditMode]);

    const handleFileSelect = (e) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            setSelectedFile(file);
            setPreviewUrl(URL.createObjectURL(file));
        }
    };

    const handleChange = (e) => {
        const { name, value, type } = e.target;
        const processedValue = type === 'number' ? (value === '' ? '' : parseFloat(value)) : value;
        onFormChange(name, processedValue);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        
        const payload = new FormData();
        payload.append('name', formData.name);
        payload.append('sku', formData.sku || '');
        payload.append('manufacturer', formData.manufacturer || '');
        
        payload.append('category', formData.category);
        payload.append('itemType', formData.itemType || 'Misc/Consumable');
        payload.append('reorderLevel', formData.reorderLevel);
        payload.append('unitCost', formData.unitCost);
        if (formData.buyAgainLink) payload.append('buyAgainLink', formData.buyAgainLink);

        if (selectedFile) {
            payload.append('image', selectedFile);
        } else if (formData.imageUrl) {
            payload.append('imageUrl', formData.imageUrl);
        }

        onSave(payload);
    };

    return (
        <form id="master-item-form" onSubmit={handleSubmit} className="master-item-form">
            <div className="form-panel-header">
                <h3>{isEditMode ? 'Edit Master Item' : 'Add New Item'}</h3>
                <button type="button" onClick={onCancel} className="modal-close-btn"><FiX /></button>
            </div>

            <div className="form-panel-body">
                <div className="form-group">
                    <label>Item Name</label>
                    <input type="text" name="name" value={formData.name} onChange={handleChange} required autoFocus />
                </div>
                
                <div className="form-group">
                    <label>Manufacturer</label>
                    <input 
                        type="text" 
                        name="manufacturer" 
                        value={formData.manufacturer || ''} 
                        onChange={handleChange} 
                        placeholder="e.g. Ubiquiti, Honeywell"
                    />
                </div>

                <div className="form-group">
                    <label>SKU (Optional)</label>
                    <input 
                        type="text" 
                        name="sku" 
                        value={formData.sku} 
                        onChange={handleChange} 
                        placeholder="e.g. PART-123"
                    />
                </div>

                <div className="form-group">
                    <label>Item Type</label>
                    <select name="itemType" value={formData.itemType || 'Misc/Consumable'} onChange={handleChange} className="form-select">
                        <option value="Misc/Consumable">Misc / Consumable</option>
                        <option value="High Value Equipment">High Value Equipment (Requires S/N)</option>
                        <option value="Assembly">Assembly</option>
                    </select>
                </div>

                <div className="toggle-advanced-wrapper">
                    <button type="button" className="toggle-advanced-btn" onClick={() => setIsAdvancedView(!isAdvancedView)}>
                        {isAdvancedView ? <FiChevronUp/> : <FiChevronDown/>}
                        {isAdvancedView ? 'Hide Extra Options' : 'Show Extra Options (Image, Cost, etc.)'}
                    </button>
                </div>

                <div className={`advanced-options-container ${isAdvancedView ? 'expanded' : ''}`}>
                    <div className="advanced-options-inner">
                        <div className="modal-image-preview-wrapper">
                            {previewUrl ? (
                                <img 
                                    src={previewUrl} 
                                    alt="Item Preview" 
                                    className="modal-image-preview" 
                                    onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} 
                                />
                            ) : null}
                            <div className="modal-image-placeholder" style={{ display: previewUrl ? 'none' : 'flex' }}>
                                <FiImage size={32} />
                                <span>No Image</span>
                            </div>
                        </div>
                        
                        <div className="form-group">
                            <CustomFileUpload 
                                label={selectedFile ? "Change Image" : "Upload Image"}
                                onChange={handleFileSelect}
                                accept="image/*"
                                className="btn-block"
                                variant="secondary"
                            />
                        </div>

                        <div className="form-group">
                            <label>Category</label>
                            <select name="category" value={formData.category} onChange={handleChange}>
                                <option>Parts</option>
                                <option>Tools</option>
                                <option>Filters</option>
                            </select>
                        </div>
                       
                        <div className="form-row">
                            <div className="form-group">
                                <label>Reorder Level</label>
                                <input type="number" name="reorderLevel" min="0" value={formData.reorderLevel} onChange={handleChange} required />
                            </div>
                            <div className="form-group">
                                <label>Unit Cost ($)</label>
                                <input type="number" name="unitCost" min="0" step="0.01" value={formData.unitCost} onChange={handleChange} required />
                            </div>
                        </div>

                        <div className="form-group">
                            <label>Buy Again Link (Optional)</label>
                            <input type="url" name="buyAgainLink" placeholder="https://example.com/product/..." value={formData.buyAgainLink || ''} onChange={handleChange} />
                        </div>
                    </div>
                </div>
            </div>

            <div className="form-panel-footer">
                <button type="button" className="btn btn-secondary" onClick={onCancel}>Cancel</button>
                <button type="submit" form="master-item-form" className="btn btn-primary">{isEditMode ? 'Save Changes' : 'Add Item'}</button>
            </div>
        </form>
    );
};

// =================================================================================
// MAIN PAGE COMPONENT
// =================================================================================
const MasterInventory = ({ masterInventoryItems, onAddMasterItem, onUpdateMasterItem, onDeleteMultipleMasterItems }) => {
    const navigate = useNavigate();
    const isMobile = useIsMobile(); 

    const initialFormState = { 
        name: '', 
        manufacturer: '', 
        sku: '', 
        itemType: 'Misc/Consumable', 
        category: 'Parts', 
        reorderLevel: 10, 
        unitCost: 0, 
        buyAgainLink: '', 
        imageUrl: '' 
    };

    const [mode, setMode] = useState('view');
    const [currentItemId, setCurrentItemId] = useState(null);
    const [formData, setFormData] = useState(initialFormState);
    const [qrItem, setQrItem] = useState(null);
    
    // State to track selected items (Desktop) and Search Term (Mobile)
    const [selectedIds, setSelectedIds] = useState([]);
    const [mobileSearchTerm, setMobileSearchTerm] = useState('');

    const filteredMobileItems = useMemo(() => {
        if (!mobileSearchTerm) return masterInventoryItems;
        const lower = mobileSearchTerm.toLowerCase();
        return masterInventoryItems.filter(i => 
            i.name.toLowerCase().includes(lower) || 
            (i.sku && i.sku.toLowerCase().includes(lower)) ||
            (i.manufacturer && i.manufacturer.toLowerCase().includes(lower))
        );
    }, [masterInventoryItems, mobileSearchTerm]);

    const handleSelectOne = (id) => {
        setSelectedIds(prev => 
            prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
        );
    };

    const handleSelectAll = (isChecked, idsOnPage) => {
        if (isChecked) {
            setSelectedIds(prev => {
                const uniqueIds = new Set([...prev, ...idsOnPage]);
                return Array.from(uniqueIds);
            });
        } else {
            setSelectedIds(prev => prev.filter(id => !idsOnPage.includes(id)));
        }
    };

    const handleDeleteSelected = async (ids) => {
        await onDeleteMultipleMasterItems(ids);
        setSelectedIds([]);
    };

    const columns = [
        { header: 'Image', accessor: 'imageUrl', render: (item) => (
            <div className="list-view-image-thumbnail-wrapper">
                {item.imageUrl ? (
                    <>
                        <img src={item.imageUrl} alt={item.name} className="list-view-image-thumbnail" />
                        <div className="list-view-image-tooltip"><img src={item.imageUrl} alt={`${item.name} preview`} /></div>
                    </>
                ) : (
                    <FiImage className="list-view-image-placeholder-icon" />
                )}
            </div>
        )},
        { header: 'Item Name', accessor: 'name' },
        { header: 'Manufacturer', accessor: 'manufacturer', render: (item) => item.manufacturer || '-' },
        { header: 'SKU', accessor: 'sku', render: (item) => item.sku || <span style={{color: 'var(--gray-400)', fontStyle: 'italic'}}>N/A</span> },
        { header: 'Type', accessor: 'itemType' },
        { header: 'Category', accessor: 'category' },
        { header: 'Reorder Lvl', accessor: 'reorderLevel' },
        { header: 'Unit Cost', render: (item) => `$${item.unitCost.toFixed(2)}` },
        { header: 'Last Updated', render: (item) => new Date(item.updatedAt).toLocaleDateString() },
        { 
            header: 'QR', 
            render: (item) => (
                <button 
                    className="btn btn-secondary btn-sm" 
                    onClick={(e) => { e.stopPropagation(); setQrItem(item); }}
                    title="View QR Code"
                >
                    <FiGrid />
                </button>
            ) 
        }
    ];

    const handleAddNew = () => {
        setMode('add');
        setCurrentItemId(null);
        setFormData(initialFormState);
    };

    const handleAddAssembly = () => {
        setMode('add');
        setCurrentItemId(null);
        setFormData({ 
            ...initialFormState, 
            name: 'New Assembly', 
            itemType: 'Assembly',
            category: 'Parts' 
        });
    };

    const handleEdit = (itemId) => {
        const item = masterInventoryItems.find(i => i._id === itemId);
        if (item) {
            setMode('edit');
            setCurrentItemId(item._id);
            setFormData({ ...initialFormState, ...item });
        }
    };

    const handleCancel = () => {
        setMode('view');
        setCurrentItemId(null);
        setFormData(initialFormState);
    };

    const handleFormChange = (name, value) => {
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSave = (payload) => {
        if (mode === 'edit' && currentItemId) {
            onUpdateMasterItem(currentItemId, payload);
        } else {
            onAddMasterItem(payload);
        }
        handleCancel(); 
    };

    return (
        <div className="page-container">
            <QRCodeModal isOpen={!!qrItem} onClose={() => setQrItem(null)} item={qrItem} />

            <div className="page-header" style={{ alignItems: isMobile ? 'flex-start' : 'center', flexDirection: isMobile ? 'column' : 'row', gap: '1rem' }}>
                <div className="header-left-details">
                    <button onClick={() => navigate('/inventory')} className="back-btn" title="Back to Inventory Management">
                        <FiArrowLeft />
                    </button>
                    <div>
                        <h2 className="page-title">Master Inventory List</h2>
                        <span className="page-subtitle">Define all reusable inventory items here.</span>
                    </div>
                </div>
                <div className="page-actions" style={{ width: isMobile ? '100%' : 'auto', flexWrap: 'wrap' }}>
                    <button className="btn btn-secondary" onClick={handleAddAssembly} style={{ flex: isMobile ? 1 : 'auto', justifyContent: 'center' }}>
                        <FiLayers className="btn-icon" /> Create Assembly
                    </button>
                    <button className="btn btn-primary" onClick={handleAddNew} style={{ flex: isMobile ? 1 : 'auto', justifyContent: 'center' }}>
                        <FiPlus className="btn-icon" /> Add Master Item
                    </button>
                </div>
            </div>

            <div className={`master-inventory-layout ${mode !== 'view' ? 'form-visible' : ''}`}>
                <div className="list-panel">
                    {/* ✅ MOBILE VIEW: List of Cards */}
                    {isMobile ? (
                        <div style={{ padding: '0.5rem 0' }}>
                            <div className="search-input-wrapper" style={{ marginBottom: '1.25rem' }}>
                                <FiSearch className="search-icon" />
                                <input 
                                    type="search" 
                                    className="search-input" 
                                    placeholder="Search by name, manufacturer or SKU..." 
                                    value={mobileSearchTerm}
                                    onChange={e => setMobileSearchTerm(e.target.value)}
                                />
                            </div>
                            <div className="mobile-inventory-list" style={{ padding: 0 }}>
                                {filteredMobileItems.length > 0 ? (
                                    filteredMobileItems.map(item => (
                                        <MobileMasterItemCard 
                                            key={item._id} 
                                            item={item} 
                                            onClick={() => handleEdit(item._id)} 
                                            onShowQR={(i) => setQrItem(i)} 
                                        />
                                    ))
                                ) : (
                                    <div className="no-tasks-message"><p>No master items found.</p></div>
                                )}
                            </div>
                        </div>
                    ) : (
                    /* ✅ DESKTOP VIEW: Standard List Table */
                        <ListView
                            items={masterInventoryItems}
                            columns={columns}
                            onNewItem={null}
                            onDeleteItems={handleDeleteSelected}
                            onItemClick={handleEdit}
                            searchKeys={['name', 'sku', 'manufacturer']}
                            searchPlaceholder="Search by name, manufacturer or SKU..."
                            itemNoun="master item"
                            enableSelection={true}
                            selectedIds={selectedIds}
                            onSelectOne={handleSelectOne}
                            onSelectAll={handleSelectAll}
                        />
                    )}
                </div>

                <div className="form-panel" style={{ 
                    borderRadius: '8px', 
                    border: mode !== 'view' ? '1px solid var(--gray-200)' : '1px solid transparent', 
                    boxShadow: mode !== 'view' ? '0 4px 12px rgba(0,0,0,0.05)' : 'none',
                    transform: mode !== 'view' ? 'translateX(0)' : 'translateX(20px)',
                    opacity: mode !== 'view' ? 1 : 0,
                    transition: 'all 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
                    backgroundColor: 'var(--white)',
                    overflow: 'hidden'
                }}>
                    <MasterItemForm
                        formData={formData}
                        onFormChange={handleFormChange}
                        onSave={handleSave}
                        onCancel={handleCancel}
                        isEditMode={mode === 'edit'}
                    />
                </div>
            </div>
        </div>
    );
};

export default MasterInventory;